import { css, StyleSheet } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import type {
	IFileAttachmentWithURL,
	IImpersonationContext,
	IRawRichTextContentState,
} from '../../../../extViewmodels';
import type { IImpersonationContextComponentProps } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useHostedBlogPostPreviewQuery } from '../../../../queries';
import { SimpleTooltip, TooltipProvider } from '../../../components/Tooltip';
import { s as tooltipStyles } from '../../../components/Tooltip/styles';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { THEME } from '../../../components-v2/Theme/theme';
import { useBlogPostEditorContext } from './hooks';

interface IBlogPostPreviewDialogBodyProps {
	title: string;
	content?: IRawRichTextContentState;
	mainImage?: IFileAttachmentWithURL | null;
	author?: string;
	impersonationContext?: IImpersonationContext;
}

const BlogPostPreviewDialogBody: React.FC<IBlogPostPreviewDialogBodyProps> = ({
	title,
	content,
	mainImage,
	author,
	impersonationContext,
}) => {
	const query = useHostedBlogPostPreviewQuery({ author, content, impersonationContext, mainImage, title });

	return (
		<DialogBody>
			{query.isLoading ? (
				<div className={css(s.loading)}>Loading preview…</div>
			) : (
				<iframe title='Blog post preview' className={css(s.frame)} srcDoc={query.data} sandbox='' />
			)}
		</DialogBody>
	);
};

export const BlogPostPreviewAction = observer(function BlogPostPreviewAction({
	impersonationContext,
}: IImpersonationContextComponentProps) {
	const userSession = useUserSession();
	const { state } = useBlogPostEditorContext();
	const [open, setOpen] = React.useState(false);

	const isImpersonating = impersonationContext?.isValid ?? false;
	const blogFeature = isImpersonating
		? impersonationContext?.account?.features?.blogFeature
		: userSession.account?.features?.blogFeature;

	const hasDraftContent = Boolean(state.title) || Boolean(state.content?.hasContent());

	let disabledReason: string | undefined;
	if (blogFeature?.dudaSiteId) {
		disabledReason =
			'Preview isn’t available for Duda-hosted blogs — your website applies its own styling when the post publishes.';
	} else if (!blogFeature?.domain) {
		disabledReason = 'Preview becomes available once your blog domain is set up.';
	} else if (!hasDraftContent) {
		disabledReason = 'Add a title or content to preview.';
	}

	return (
		<>
			{disabledReason ? (
				<TooltipProvider>
					<SimpleTooltip title={disabledReason} delayDuration={0} styles={[tooltipStyles.contentWithMaxWidth]}>
						<span tabIndex={0}>
							<Button variant='secondary' size='sm' disabled>
								Preview
							</Button>
						</span>
					</SimpleTooltip>
				</TooltipProvider>
			) : (
				<Button variant='secondary' size='sm' onClick={() => setOpen(true)}>
					Preview
				</Button>
			)}
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogPopup styles={[s.popup]}>
					<DialogHeader>
						<DialogTitle>Blog Post Preview</DialogTitle>
					</DialogHeader>
					{open ? (
						<BlogPostPreviewDialogBody
							title={state.title}
							content={state.content?.getRawRichTextContent()}
							mainImage={state.selectedImage}
							author={state.sendFromUser?.id}
							impersonationContext={impersonationContext?.toJs()}
						/>
					) : null}
					<DialogFooter>
						<Button variant='primary' onClick={() => setOpen(false)}>
							Okay
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</>
	);
});

const s = StyleSheet.create({
	popup: {
		maxWidth: 1080,
		width: '92vw',
	},
	loading: {
		padding: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	frame: {
		width: '100%',
		height: '65vh',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
	},
});
